import { isRetryableError, retryDelayMs } from "@rakazo/core";
import type { ArtifactTarget } from "./artifact-open";

type LiveVoiceInputBase = {
  spaceId: string | null;
  target: ArtifactTarget;
  callId: string;
  utteranceId: number;
};
export type LiveVoiceTextInput = LiveVoiceInputBase & {
  text: string;
  audioBase64?: never;
  mimeType?: never;
};
export type LiveVoiceAudioInput = LiveVoiceInputBase & {
  audioBase64: string;
  mimeType: "audio/wav";
  text?: never;
};
export type LiveVoiceInput = LiveVoiceTextInput | LiveVoiceAudioInput;
export type LiveInputReceipt = { taskId: string; runId: string };
type ErrorKind = "invalid" | "conflict" | "capacity" | "storage" | "paused" | "permanent";
export class LiveInputError extends Error {
  constructor(
    readonly kind: ErrorKind,
    message: string,
    options?: ErrorOptions,
    readonly blockedBy?: LiveVoiceInput,
    readonly silent = false,
  ) {
    super(message, options);
    this.name = "LiveInputError";
  }
}
export type StoredLiveInput = {
  id: string;
  scope: string;
  fingerprint: string;
  input?: LiveVoiceInput;
  /** "empty" marks a recording the server heard no speech in: settled, payload erased.
   * "empty" позначає запис без мовлення: завершений, без даних. */
  state: "pending" | "failed" | "delivered" | "empty";
  receipt?: LiveInputReceipt;
  updatedAt: number;
};
/** Atomic local storage boundary. / Межа атомарного локального сховища. */
export interface LiveInputStorage {
  transact<T>(change: (rows: StoredLiveInput[]) => T): Promise<T>;
}

const DB_NAME = "hrokbot-live-input-v1";
const STORE_NAME = "inputs";
const MAX_TEXT = 20_000;
const MAX_AUDIO_BYTES = 8 * 1024 * 1024;
const MAX_AUDIO_SCOPE = 32 * 1024 * 1024;
const MAX_AUDIO_TOTAL = 64 * 1024 * 1024;
const MAX_PENDING_SCOPE = 64;
const MAX_PENDING_TOTAL = 256;
const MAX_RECEIPTS_SCOPE = 128;
const MAX_RECEIPTS_TOTAL = 512;
const STALE_SCOPE_MS = 7 * 24 * 60 * 60 * 1000;

export function createLiveInputStorage(
  factory: IDBFactory = globalThis.indexedDB,
): LiveInputStorage {
  let connection: Promise<IDBDatabase> | undefined;
  const open = () =>
    (connection ??= new Promise<IDBDatabase>((resolve, reject) => {
      if (!factory) {
        reject(new Error("IndexedDB unavailable / IndexedDB недоступна"));
        return;
      }
      const request = factory.open(DB_NAME, 1);
      request.onupgradeneeded = () =>
        request.result.createObjectStore(STORE_NAME, { keyPath: "id" });
      request.onerror = () => reject(request.error);
      request.onblocked = () =>
        reject(new Error("Local storage blocked / Локальне сховище заблоковано"));
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => {
          db.close();
          connection = undefined;
        };
        resolve(db);
      };
    }).catch((error) => {
      connection = undefined;
      throw error;
    }));
  return {
    async transact<T>(change: (rows: StoredLiveInput[]) => T): Promise<T> {
      try {
        const db = await open();
        return await new Promise<T>((resolve, reject) => {
          const transaction = db.transaction(STORE_NAME, "readwrite");
          const store = transaction.objectStore(STORE_NAME);
          const request = store.getAll();
          let result: T;
          let failure: unknown;
          request.onsuccess = () => {
            try {
              const rows = request.result as StoredLiveInput[];
              for (const row of rows) {
                if (row.input) {
                  Object.freeze(row.input.target);
                  Object.freeze(row.input);
                }
              }
              const original = new Map(
                rows.map((row) => [
                  row.id,
                  { ...row, receipt: row.receipt ? { ...row.receipt } : undefined },
                ]),
              );
              result = change(rows);
              for (const row of rows) {
                const previous = original.get(row.id);
                original.delete(row.id);
                if (
                  !previous ||
                  previous.scope !== row.scope ||
                  previous.fingerprint !== row.fingerprint ||
                  previous.state !== row.state ||
                  previous.updatedAt !== row.updatedAt ||
                  previous.input !== row.input ||
                  previous.receipt?.taskId !== row.receipt?.taskId ||
                  previous.receipt?.runId !== row.receipt?.runId
                )
                  store.put(row);
              }
              for (const id of original.keys()) store.delete(id);
            } catch (error) {
              failure = error;
              transaction.abort();
            }
          };
          transaction.oncomplete = () => resolve(result);
          transaction.onabort = () => reject(failure ?? transaction.error ?? request.error);
          transaction.onerror = () => reject(failure ?? transaction.error ?? request.error);
        });
      } catch (error) {
        if (error instanceof LiveInputError) throw error;
        throw new LiveInputError("storage", "Voice input not saved / Голосовий ввід не збережено", {
          cause: error,
        });
      }
    },
  };
}

type Options = {
  scope: string;
  send: (input: LiveVoiceInput) => Promise<LiveInputReceipt>;
  onError?: (error: LiveInputError, input?: LiveVoiceInput) => void;
  storage?: LiveInputStorage;
};
let activeScope: string | null = null;
const outboxes = new Map<string, LiveInputOutbox>();
const instances = new Set<LiveInputOutbox>();
let defaultStorage: LiveInputStorage | undefined;

/** Set from authenticated app lifetime, never from the voice window lifetime.
 * Встановлюється життям авторизованого застосунку, а не голосового вікна. */
export function setLiveInputScope(scope: string | null): void {
  if (scope === activeScope) return;
  activeScope = scope;
  for (const outbox of instances) outbox.scopeChanged();
}

export function getLiveInputOutbox(options: Options): LiveInputOutbox {
  let outbox = outboxes.get(options.scope);
  if (!outbox) {
    outbox = createLiveInputOutbox(options);
    outboxes.set(options.scope, outbox);
  } else outbox.configure(options);
  return outbox;
}

/** The factory also permits deterministic offline storage tests.
 * Фабрика також дозволяє детерміновані автономні перевірки сховища. */
export function createLiveInputOutbox(options: Options): LiveInputOutbox {
  const outbox = new LiveInputOutbox(options);
  instances.add(outbox);
  return outbox;
}

function immutable(input: LiveVoiceInput): LiveVoiceInput {
  const target = input.target;
  if (
    !target ||
    typeof target !== "object" ||
    "botId" in target === "groupId" in target ||
    typeof ("botId" in target ? target.botId : target.groupId) !== "string" ||
    !("botId" in target ? target.botId : target.groupId) ||
    ("botId" in target ? target.botId : target.groupId).length > 160 ||
    (input.spaceId !== null &&
      (typeof input.spaceId !== "string" || !input.spaceId || input.spaceId.length > 160)) ||
    typeof input.callId !== "string" ||
    !input.callId ||
    input.callId.length > 160 ||
    !Number.isSafeInteger(input.utteranceId) ||
    input.utteranceId < 0
  )
    throw new LiveInputError(
      "invalid",
      "Invalid final voice input / Некоректний фінальний голосовий ввід",
    );
  const base = {
    spaceId: input.spaceId,
    target: Object.freeze(
      "botId" in target ? { botId: target.botId } : { groupId: target.groupId },
    ),
    callId: input.callId,
    utteranceId: input.utteranceId,
  };
  if (typeof input.audioBase64 === "string") {
    if (input.text !== undefined || input.mimeType !== "audio/wav") throw invalid();
    validateAudio(input.audioBase64);
    return Object.freeze({ ...base, audioBase64: input.audioBase64, mimeType: "audio/wav" });
  }
  if (
    input.audioBase64 !== undefined ||
    input.mimeType !== undefined ||
    typeof input.text !== "string" ||
    !input.text.trim() ||
    input.text.length > MAX_TEXT
  )
    throw invalid();
  return Object.freeze({ ...base, text: input.text });
}

function invalid(): LiveInputError {
  return new LiveInputError(
    "invalid",
    "Invalid final voice input / Некоректний фінальний голосовий ввід",
  );
}

function audioBytes(input: LiveVoiceInput): number {
  const value = input.audioBase64;
  return value
    ? (value.length / 4) * 3 - (value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0)
    : 0;
}

function validateAudio(value: string): void {
  if (
    !value ||
    value.length > 4 * Math.ceil(MAX_AUDIO_BYTES / 3) ||
    value.length % 4 ||
    /[^A-Za-z0-9+/=]/.test(value)
  )
    throw invalid();
  const paddingAt = value.indexOf("=");
  const padding = paddingAt < 0 ? 0 : value.length - paddingAt;
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  if (
    padding > 2 ||
    (padding && value.slice(paddingAt) !== "=".repeat(padding)) ||
    (padding === 2 && (alphabet.indexOf(value[value.length - 3]!) & 15) !== 0) ||
    (padding === 1 && (alphabet.indexOf(value[value.length - 2]!) & 3) !== 0)
  )
    throw invalid();
  const size = (value.length / 4) * 3 - padding;
  if (size < 44 || size > MAX_AUDIO_BYTES) throw invalid();
  const header = atob(value.slice(0, 16));
  const declared =
    header.charCodeAt(4) +
    header.charCodeAt(5) * 256 +
    header.charCodeAt(6) * 65_536 +
    header.charCodeAt(7) * 16_777_216;
  if (header.slice(0, 4) !== "RIFF" || header.slice(8, 12) !== "WAVE" || declared !== size - 8)
    throw invalid();
}

/** The server answered a complete recording with "no speech" (a cough, a click, an echo).
 * Сервер відповів, що в завершеному записі немає мовлення (кашель, клацання, відлуння). */
function isNoSpeech(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("data" in error)) return false;
  const { data } = error;
  return (
    typeof data === "object" && data !== null && "reason" in data && data.reason === "no_speech"
  );
}

/** Delivered receipts and no-speech markers hold no payload and block nothing.
 * Квитанції та позначки «без мовлення» не мають даних і нічого не блокують. */
function isSettled(row: StoredLiveInput): boolean {
  return row.state === "delivered" || row.state === "empty";
}

/** Keep only the newest settled rows, per scope and overall. / Лишаємо найновіші завершені рядки. */
function pruneSettled(rows: StoredLiveInput[]): void {
  const counts = new Map<string, number>();
  const keep = new Set(
    rows
      .filter(isSettled)
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, MAX_RECEIPTS_TOTAL)
      .filter((entry) => {
        const count = (counts.get(entry.scope) ?? 0) + 1;
        counts.set(entry.scope, count);
        return count <= MAX_RECEIPTS_SCOPE;
      })
      .map((entry) => entry.id),
  );
  for (let index = rows.length - 1; index >= 0; index--)
    if (isSettled(rows[index]!) && !keep.has(rows[index]!.id)) rows.splice(index, 1);
}

function sameInput(one: LiveVoiceInput, two: LiveVoiceInput): boolean {
  return (
    one.spaceId === two.spaceId &&
    one.callId === two.callId &&
    one.utteranceId === two.utteranceId &&
    ("botId" in one.target
      ? "botId" in two.target && one.target.botId === two.target.botId
      : "groupId" in two.target && one.target.groupId === two.target.groupId) &&
    one.text === two.text &&
    one.audioBase64 === two.audioBase64 &&
    one.mimeType === two.mimeType
  );
}

type EnrollmentFailure = {
  key: string;
  utteranceId: number;
  fingerprint: string | Promise<string>;
  input?: LiveVoiceInput;
};

async function fingerprint(input: LiveVoiceInput): Promise<string> {
  try {
    const bytes = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(JSON.stringify(input)),
    );
    return Array.from(new Uint8Array(bytes), (value) => value.toString(16).padStart(2, "0")).join(
      "",
    );
  } catch (error) {
    throw new LiveInputError("storage", "Voice input not saved / Голосовий ввід не збережено", {
      cause: error,
    });
  }
}

export class LiveInputOutbox {
  private storage: LiveInputStorage;
  private options: Options;
  private sending = new Map<
    string,
    { input: LiveVoiceInput; promise: Promise<LiveInputReceipt | null> }
  >();
  private preparing: Promise<unknown> = Promise.resolve();
  private lanes = new Map<string, Promise<void>>();
  private enrollmentFailures = new Map<string, EnrollmentFailure>();
  private sleepers = new Map<ReturnType<typeof setTimeout>, () => void>();

  constructor(options: Options) {
    if (!options.scope || options.scope.length > 2048)
      throw new LiveInputError("paused", "Voice session unavailable / Голосова сесія недоступна");
    this.options = options;
    if (!options.storage && !defaultStorage) defaultStorage = createLiveInputStorage();
    this.storage = options.storage ?? defaultStorage!;
  }

  configure(options: Options): void {
    this.options = {
      ...this.options,
      send: options.send,
      ...(options.onError ? { onError: options.onError } : {}),
    };
  }

  scopeChanged(): void {
    for (const [timer, wake] of this.sleepers) {
      clearTimeout(timer);
      wake();
    }
    this.sleepers.clear();
  }

  async pending(): Promise<readonly LiveVoiceInput[]> {
    return this.storage.transact((rows) =>
      rows
        .filter((row) => row.scope === this.options.scope && row.input)
        .map((row) => immutable(row.input!)),
    );
  }

  async resume(): Promise<void> {
    const rows = await this.storage.transact((all) => {
      // Rows of another auth scope are never retried from here; drop those nobody came back for.
      // Рядки іншого облікового запису тут не повторюються; прибираємо ті, до яких не повернулись.
      const cutoff = Date.now() - STALE_SCOPE_MS;
      for (let index = all.length - 1; index >= 0; index--)
        if (all[index]!.scope !== this.options.scope && all[index]!.updatedAt < cutoff)
          all.splice(index, 1);
      return all
        .filter((row) => row.scope === this.options.scope && !isSettled(row) && row.input)
        .sort((a, b) => a.input!.utteranceId - b.input!.utteranceId);
    });
    for (const row of rows) void this.enqueue(row.input!).catch(() => undefined);
  }

  async retry(input: LiveVoiceInput): Promise<LiveInputReceipt | null> {
    const value = immutable(input);
    const hash = await fingerprint(value);
    await this.storage.transact((rows) => {
      const row = rows.find((item) => item.id === this.key(value));
      if (row && row.fingerprint !== hash) throw this.conflict();
      if (row?.state === "failed") row.state = "pending";
    });
    const receipt = await this.submit(value, true);
    void this.resume().catch((error: unknown) => {
      this.options.onError?.(
        error instanceof LiveInputError
          ? error
          : new LiveInputError("storage", "Voice input not saved / Голосовий ввід не збережено", {
              cause: error,
            }),
      );
    });
    return receipt;
  }

  /** Resolves with the receipt, or null when the server found no speech in the recording: that
   * utterance is settled and nothing is sent to the bot.
   * Повертає квитанцію або null, якщо в записі немає мовлення: нічого не надсилається. */
  async enqueue(input: LiveVoiceInput): Promise<LiveInputReceipt | null> {
    return this.submit(input, false);
  }

  private async submit(input: LiveVoiceInput, retry: boolean): Promise<LiveInputReceipt | null> {
    let value: LiveVoiceInput | undefined;
    try {
      value = immutable(input);
      const id = this.key(value);
      const current = this.sending.get(id);
      if (current) {
        if (!sameInput(current.input, value)) throw this.conflict();
        return await current.promise;
      }
      const final = value;
      if (!this.canAdmit(final, id)) {
        const lane = this.lane(final);
        if (this.enrollmentFailures.has(lane))
          throw new LiveInputError(
            "permanent",
            "Earlier voice input needs retry / Попередній голосовий ввід потребує повтору",
            undefined,
            undefined,
            true,
          );
        this.enrollmentFailures.set(lane, {
          key: id,
          utteranceId: final.utteranceId,
          fingerprint: fingerprint(final).catch(() => ""),
        });
        throw new LiveInputError(
          "capacity",
          "Voice outbox full / Сховище голосового вводу заповнене",
        );
      }
      // Enroll in invocation order before asynchronous hashing can reorder inputs.
      // Додаємо за порядком виклику, щоб асинхронне хешування не переставило репліки.
      const prepared = this.preparing.then(async () => {
        const lane = this.lane(final);
        const knownBarrier = this.enrollmentFailures.get(lane);
        const barrier =
          knownBarrier && knownBarrier.utteranceId <= final.utteranceId ? knownBarrier : undefined;
        let hash = "";
        if (barrier && !barrier.input && barrier.key === id) hash = await fingerprint(final);
        const same =
          barrier &&
          barrier.key === id &&
          (barrier.input ? sameInput(barrier.input, final) : (await barrier.fingerprint) === hash);
        if (barrier && retry && barrier.key === id && !same) throw this.conflict();
        if (barrier && (!retry || !same)) {
          if (!barrier.input)
            throw new LiveInputError(
              "permanent",
              "Earlier voice input needs retry / Попередній голосовий ввід потребує повтору",
              undefined,
              undefined,
              true,
            );
          try {
            await this.persist(final, await fingerprint(final), {
              input: barrier.input,
              fingerprint: (await barrier.fingerprint) || (await fingerprint(barrier.input)),
            });
          } catch (error) {
            throw new LiveInputError(
              error instanceof LiveInputError ? error.kind : "storage",
              "Voice input not saved / Голосовий ввід не збережено",
              { cause: error },
              barrier.input,
            );
          }
          throw this.blocked(barrier.input);
        }
        try {
          hash ||= await fingerprint(final);
          const row = await this.persist(final, hash);
          if (barrier) this.enrollmentFailures.delete(lane);
          return row;
        } catch (error) {
          if (
            !(error instanceof LiveInputError) ||
            error.kind === "storage" ||
            error.kind === "capacity"
          )
            this.rememberFailure(lane, id, hash, final, barrier, error);
          throw error;
        }
      });
      this.preparing = prepared.catch(() => undefined);
      const promise = prepared
        .then((row) => this.queue(final, row))
        .finally(() => this.sending.delete(id));
      this.sending.set(id, { input: final, promise });
      return await promise;
    } catch (error) {
      const failure =
        error instanceof LiveInputError
          ? error
          : new LiveInputError("storage", "Voice input not saved / Голосовий ввід не збережено", {
              cause: error,
            });
      if (!failure.silent) this.options.onError?.(failure, failure.blockedBy ?? value);
      throw failure;
    }
  }

  private key(input: LiveVoiceInput): string {
    return JSON.stringify([this.options.scope, input.callId, input.utteranceId]);
  }

  private lane(input: LiveVoiceInput): string {
    return JSON.stringify([this.options.scope, input.spaceId, input.target, input.callId]);
  }

  private blocked(input: LiveVoiceInput): LiveInputError {
    return new LiveInputError(
      "permanent",
      "Earlier voice input needs retry / Попередній голосовий ввід потребує повтору",
      undefined,
      input,
    );
  }

  private volatileInputs(
    rows: StoredLiveInput[] = [],
  ): Array<{ id: string; scope: string; input: LiveVoiceInput }> {
    const seen = new Set(rows.map((row) => row.id));
    const result: Array<{ id: string; scope: string; input: LiveVoiceInput }> = [];
    for (const outbox of instances)
      for (const failure of outbox.enrollmentFailures.values()) {
        if (failure.input && !seen.has(failure.key)) {
          seen.add(failure.key);
          result.push({ id: failure.key, scope: outbox.options.scope, input: failure.input });
        }
      }
    return result;
  }

  private canAdmit(input: LiveVoiceInput, id: string): boolean {
    const held = new Map(this.volatileInputs().map((item) => [item.id, item]));
    for (const outbox of instances)
      for (const [key, sending] of outbox.sending)
        held.set(key, { id: key, scope: outbox.options.scope, input: sending.input });
    held.delete(id);
    const all = [...held.values()];
    const own = all.filter((item) => item.scope === this.options.scope);
    return (
      all.length < MAX_PENDING_TOTAL &&
      own.length < MAX_PENDING_SCOPE &&
      all.reduce((sum, item) => sum + audioBytes(item.input), audioBytes(input)) <=
        MAX_AUDIO_TOTAL &&
      own.reduce((sum, item) => sum + audioBytes(item.input), audioBytes(input)) <= MAX_AUDIO_SCOPE
    );
  }

  private rememberFailure(
    lane: string,
    id: string,
    hash: string,
    input: LiveVoiceInput,
    previous: EnrollmentFailure | undefined,
    error: unknown,
  ): void {
    if (previous) return;
    const held = this.volatileInputs().filter((item) => item.id !== id);
    const size = audioBytes(input);
    const retain =
      !(error instanceof LiveInputError && error.kind === "capacity") &&
      held.length < MAX_PENDING_TOTAL &&
      held.filter((item) => item.scope === this.options.scope).length < MAX_PENDING_SCOPE &&
      held.reduce((sum, item) => sum + audioBytes(item.input), size) <= MAX_AUDIO_TOTAL &&
      held
        .filter((item) => item.scope === this.options.scope)
        .reduce((sum, item) => sum + audioBytes(item.input), size) <= MAX_AUDIO_SCOPE;
    this.enrollmentFailures.set(lane, {
      key: id,
      utteranceId: input.utteranceId,
      fingerprint: hash,
      ...(retain ? { input } : {}),
    });
  }

  private queue(input: LiveVoiceInput, row: StoredLiveInput): Promise<LiveInputReceipt | null> {
    if (row.state === "delivered" && row.receipt) return Promise.resolve(row.receipt);
    if (row.state === "empty") return Promise.resolve(null);
    const lane = this.lane(input);
    const previous = this.lanes.get(lane) ?? Promise.resolve();
    const delivery = previous.then(async () => {
      while (true) {
        const next = await this.storage.transact((rows) => {
          const current = rows.find((item) => item.id === this.key(input));
          if (current?.state === "empty" || (current?.state === "delivered" && current.receipt))
            return current;
          return rows
            .filter(
              (item) =>
                item.scope === this.options.scope &&
                item.input &&
                this.lane(item.input) === lane &&
                item.input.utteranceId <= input.utteranceId,
            )
            .sort((a, b) => a.input!.utteranceId - b.input!.utteranceId)[0];
        });
        if (!next)
          throw new LiveInputError(
            "storage",
            "Voice input unavailable / Голосовий ввід недоступний",
          );
        if (next.state === "delivered" && next.receipt) return next.receipt;
        if (next.state === "empty") return null;
        if (next.state === "failed") throw this.blocked(next.input!);
        const final = immutable(next.input!);
        const receipt = await this.deliver(final, await fingerprint(final));
        if (next.id === this.key(input)) return receipt;
      }
    });
    const settled = delivery.then(
      () => undefined,
      () => undefined,
    );
    this.lanes.set(lane, settled);
    void settled.then(() => {
      if (this.lanes.get(lane) === settled) this.lanes.delete(lane);
    });
    return delivery;
  }

  private conflict(): LiveInputError {
    return new LiveInputError(
      "conflict",
      "Final voice input cannot change / Фінальний голосовий ввід не можна змінити",
    );
  }

  private async persist(
    input: LiveVoiceInput,
    hash: string,
    barrier?: { input: LiveVoiceInput; fingerprint: string },
  ): Promise<StoredLiveInput> {
    return this.storage.transact((rows) => {
      const add = (value: LiveVoiceInput, fingerprint: string, state: "pending" | "failed") => {
        const id = this.key(value);
        const existing = rows.find((item) => item.id === id);
        if (existing) {
          if (existing.fingerprint !== fingerprint) throw this.conflict();
          return existing;
        }
        // Stored rows of other scopes are not this one's to bound; resume() sweeps the stale ones.
        // Збережені рядки інших облікових записів не обмежують цей; застарілі прибирає resume().
        const pending = rows.filter(
          (item) => item.scope === this.options.scope && !isSettled(item),
        );
        const volatile = this.volatileInputs(rows).filter((item) => item.id !== id);
        const all = [
          ...pending.flatMap((item) =>
            item.input ? [{ scope: item.scope, input: item.input }] : [],
          ),
          ...volatile,
        ];
        const size = audioBytes(value);
        if (
          pending.length + volatile.length >= MAX_PENDING_TOTAL ||
          pending.length + volatile.filter((item) => item.scope === this.options.scope).length >=
            MAX_PENDING_SCOPE ||
          all.reduce((sum, item) => sum + audioBytes(item.input), size) > MAX_AUDIO_TOTAL ||
          all
            .filter((item) => item.scope === this.options.scope)
            .reduce((sum, item) => sum + audioBytes(item.input), size) > MAX_AUDIO_SCOPE
        )
          throw new LiveInputError(
            "capacity",
            "Voice outbox full / Сховище голосового вводу заповнене",
          );
        const created: StoredLiveInput = {
          id,
          scope: this.options.scope,
          fingerprint,
          input: value,
          state,
          updatedAt: Date.now(),
        };
        rows.push(created);
        return created;
      };
      if (barrier) {
        const first = add(barrier.input, barrier.fingerprint, "failed");
        if (!isSettled(first)) first.state = "failed";
      }
      return add(input, hash, "pending");
    });
  }

  private async deliver(input: LiveVoiceInput, hash: string): Promise<LiveInputReceipt | null> {
    const id = this.key(input);
    const row = await this.persist(input, hash);
    if (row.state === "delivered" && row.receipt) return row.receipt;
    if (row.state === "empty") return null;
    if (row.state === "failed")
      throw new LiveInputError(
        "permanent",
        "Voice input needs retry / Голосовий ввід потребує явного повтору",
      );
    let attempts = 0;
    while (true) {
      if (activeScope !== this.options.scope)
        throw new LiveInputError(
          "paused",
          "Voice delivery paused after account change / Доставлення голосу призупинено після зміни облікового запису",
        );
      let receipt: LiveInputReceipt;
      try {
        receipt = await this.options.send(input);
        if (
          typeof receipt?.taskId !== "string" ||
          !receipt.taskId ||
          receipt.taskId.length > 160 ||
          typeof receipt?.runId !== "string" ||
          !receipt.runId ||
          receipt.runId.length > 160
        )
          throw new LiveInputError(
            "permanent",
            "Invalid voice delivery receipt / Некоректна квитанція доставлення голосу",
          );
      } catch (error) {
        if (
          activeScope !== this.options.scope ||
          (error instanceof LiveInputError && error.kind === "paused")
        )
          throw new LiveInputError(
            "paused",
            "Voice delivery paused / Доставлення голосу призупинено",
            { cause: error },
          );
        if (isNoSpeech(error)) {
          // Nothing was said: settle it so it blocks no later utterance and is never resent.
          // Нічого не сказано: завершуємо, щоб не блокувати наступні репліки й не повторювати.
          await this.storage.transact((rows) => {
            const item = rows.find((entry) => entry.id === id);
            if (item) {
              item.state = "empty";
              item.updatedAt = Date.now();
              delete item.input;
            }
            pruneSettled(rows);
          });
          return null;
        }
        if (error instanceof LiveInputError || !isRetryableError(error)) {
          await this.storage.transact((rows) => {
            const item = rows.find((entry) => entry.id === id);
            if (item) item.state = "failed";
          });
          throw error instanceof LiveInputError
            ? error
            : new LiveInputError(
                "permanent",
                "Voice input not accepted / Голосовий ввід не прийнято",
                { cause: error },
              );
        }
        await new Promise<void>((resolve) => {
          const timer = setTimeout(
            () => {
              this.sleepers.delete(timer);
              resolve();
            },
            retryDelayMs(error, Math.min(30_000, 1000 * 2 ** Math.min(attempts++, 5))),
          );
          this.sleepers.set(timer, resolve);
        });
        continue;
      }
      await this.storage.transact((rows) => {
        const item = rows.find((entry) => entry.id === id)!;
        item.state = "delivered";
        item.receipt = { taskId: receipt.taskId, runId: receipt.runId };
        item.updatedAt = Date.now();
        delete item.input;
        pruneSettled(rows);
      });
      return receipt;
    }
  }
}
